import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { toast } from 'sonner'
import { useChangeAssignee } from '../hooks'
import type { AssigneeInput, Task } from '../types'
import { AssigneePicker } from './AssigneePicker'

interface Props {
  task: Task
  onClose: () => void
}

function toAssigneeInput(a: Task['assignee']): AssigneeInput {
  if (a.type === 'USER') return { type: 'USER', userId: a.userId, userName: a.userName }
  return { type: 'GROUP', group: a.group }
}

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Blad'
}

/**
 * Dialog do zmiany przypisania taska (user <> group). Otwierany z TaskDetailDrawer
 * klikając przycisk "Zmień" obok AssigneeChip. Mirror pattern ChangeContextDialog.
 */
export function ChangeAssigneeDialog({ task, onClose }: Props) {
  const [picked, setPicked] = useState<AssigneeInput | null>(toAssigneeInput(task.assignee))
  const mutation = useChangeAssignee(task.id)

  const handleSave = () => {
    if (!picked) return
    mutation.mutate(
      { assignee: picked },
      {
        onSuccess: onClose,
        onError: (e) => toast.error(formatError(e)),
      },
    )
  }

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[380px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">Zmień przypisanie</Dialog.Title>
          <div className="mt-4 flex flex-col gap-4 text-sm">
            <AssigneePicker value={picked} onChange={setPicked} />
            <div className="flex justify-end gap-2 mt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1 text-sm border rounded"
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={mutation.isPending || !picked}
                className="px-3 py-1 text-sm bg-sky-600 text-white rounded disabled:opacity-50"
              >
                Zapisz
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
